import { useState, useMemo } from 'react'
import { ChevronDown, ChevronRight, MoreHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { LeadTableRow } from '@/services/mockLeads'
import { RiskScoreBar } from './RiskScoreBar'
import { PatternIndicators } from './PatternIndicators'
import { LeadStatusBadge } from './LeadStatusBadge'
import { LeadActionButtons } from './LeadActionButtons'
import { LeadExpandedDetails } from './LeadExpandedDetails'

interface LeadTableProps {
  leads: LeadTableRow[]
  selectedLeads: string[]
  onSelectionChange: (leadIds: string[]) => void
  onApprove: (leadId: string) => void
  onReject: (leadId: string) => void
  onFlag: (leadId: string) => void
  onNote: (leadId: string) => void
}

export function LeadTable({
  leads,
  selectedLeads,
  onSelectionChange,
  onApprove,
  onReject,
  onFlag,
  onNote
}: LeadTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [sortConfig, setSortConfig] = useState<{
    key: keyof LeadTableRow
    direction: 'asc' | 'desc'
  }>({ key: 'timestamp', direction: 'desc' })
  
  const sortedLeads = useMemo(() => {
    return [...leads].sort((a, b) => {
      const aValue = a[sortConfig.key]
      const bValue = b[sortConfig.key]
      
      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
  }, [leads, sortConfig])
  
  const handleSort = (key: keyof LeadTableRow) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }))
  }
  
  const toggleExpanded = (leadId: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(leadId)) {
      newExpanded.delete(leadId)
    } else {
      newExpanded.add(leadId)
    }
    setExpandedRows(newExpanded)
  }
  
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange(leads.map(lead => lead.id))
    } else {
      onSelectionChange([])
    }
  }
  
  const handleSelectLead = (leadId: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedLeads, leadId])
    } else {
      onSelectionChange(selectedLeads.filter(id => id !== leadId))
    }
  }
  
  const isAllSelected = leads.length > 0 && selectedLeads.length === leads.length
  const isPartiallySelected = selectedLeads.length > 0 && selectedLeads.length < leads.length
  
  const formatTimestamp = (date: Date) => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  
  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40px]">
            <Checkbox
              checked={isAllSelected}
              onCheckedChange={handleSelectAll}
              className={isPartiallySelected ? 'data-[state=indeterminate]:bg-primary' : ''}
            />
            </TableHead>
            
            <TableHead className="w-[40px]"></TableHead>
            
            <TableHead 
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => handleSort('timestamp')}
            >
              Time
            </TableHead>
            
            <TableHead 
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => handleSort('firstName')}
            >
              Lead
            </TableHead>
            
            <TableHead 
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => handleSort('email')}
            >
              Contact
            </TableHead>
            
            <TableHead 
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => handleSort('riskScore')}
            >
              Risk Score
            </TableHead>
            
            <TableHead>Patterns</TableHead>
            
            <TableHead 
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => handleSort('status')}
            >
              Status
            </TableHead>
            
            <TableHead 
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => handleSort('source')}
            >
              Source
            </TableHead>
            
            <TableHead className="w-[200px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        
        <TableBody>
          {sortedLeads.map((lead) => {
            const isExpanded = expandedRows.has(lead.id)
            const isSelected = selectedLeads.includes(lead.id)
            
            return (
              <>
                <TableRow 
                  key={lead.id}
                  className={`cursor-pointer hover:bg-muted/30 ${
                    isSelected ? 'bg-muted/50' : ''
                  } ${
                    lead.riskScore >= 80 ? 'border-l-4 border-l-red-500' :
                    lead.riskScore >= 60 ? 'border-l-4 border-l-orange-500' :
                    lead.riskScore >= 30 ? 'border-l-4 border-l-yellow-500' :
                    'border-l-4 border-l-green-500'
                  }`}
                >
                  <TableCell>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(checked) => handleSelectLead(lead.id, !!checked)}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => toggleExpanded(lead.id)}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  </TableCell>
                  
                  <TableCell className="font-mono text-xs">
                    {formatTimestamp(lead.timestamp)}
                  </TableCell>
                  
                  <TableCell>
                    <div>
                      <div className="font-medium">
                        {lead.firstName} {lead.lastName}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">
                        {lead.id}
                      </div>
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-sm">{lead.email}</div>
                      <div className="text-xs text-muted-foreground">
                        {lead.phone}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{lead.zip}</span>
                        <Badge variant="outline" className="text-xs">
                          {lead.ipType}
                        </Badge>
                      </div>
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <RiskScoreBar 
                      score={lead.riskScore} 
                      size="sm"
                      className="w-24"
                    />
                  </TableCell>
                  
                  <TableCell>
                    <PatternIndicators 
                      patterns={lead.patterns}
                      size="sm"
                    />
                  </TableCell>
                  
                  <TableCell>
                    <LeadStatusBadge status={lead.status} size="sm" />
                  </TableCell>
                  
                  <TableCell>
                    <Badge variant="secondary" className="capitalize text-xs">
                      {lead.source}
                    </Badge>
                  </TableCell>
                  
                  <TableCell>
                    <LeadActionButtons
                      lead={lead}
                      onApprove={onApprove}
                      onReject={onReject}
                      onFlag={onFlag}
                      onNote={onNote}
                      compact
                    />
                  </TableCell>
                </TableRow>
                
                {isExpanded && (
                  <TableRow>
                    <TableCell colSpan={10} className="p-0">
                      <LeadExpandedDetails
                        lead={lead}
                        onApprove={onApprove}
                        onReject={onReject}
                        onFlag={onFlag}
                        onNote={onNote}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </>
            )
          })}
        </TableBody>
      </Table>
      
      {leads.length === 0 && (
        <div className="text-center py-12">
          <div className="text-muted-foreground mb-2">No leads found</div>
          <div className="text-sm text-muted-foreground">
            Try adjusting your filters or date range
          </div>
        </div>
      )}
    </div>
  )
}